import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, FileText, Loader2, Calendar as CalendarIcon, ArrowRight, CalendarDays, X } from 'lucide-react';
import { format, addDays, parseISO, differenceInDays, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Checkbox } from '../components/ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [deleteAllStep, setDeleteAllStep] = useState(0); // 0=closed, 1=first confirm, 2=final confirm
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [formData, setFormData] = useState({
    invoice_date: format(new Date(), 'yyyy-MM-dd'),
    customer_id: '',
    description: '',
    amount: '',
    extra_days: 0,
    payment_basis: 'terms'
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invoicesRes, customersRes] = await Promise.all([
        axios.get(`${API}/invoices`),
        axios.get(`${API}/customers`)
      ]);
      setInvoices(invoicesRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (invoice = null) => {
    if (invoice) {
      setEditingInvoice(invoice);
      setFormData({
        invoice_date: invoice.invoice_date,
        customer_id: invoice.customer_id,
        description: invoice.description || '',
        amount: invoice.amount.toString(),
        extra_days: invoice.extra_days,
        payment_basis: invoice.payment_basis || 'terms'
      });
    } else {
      setEditingInvoice(null);
      setFormData({
        invoice_date: format(new Date(), 'yyyy-MM-dd'),
        customer_id: customers[0]?.id || '',
        description: '',
        amount: '',
        extra_days: 0,
        payment_basis: 'terms'
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        extra_days: parseInt(formData.extra_days) || 0
      };

      if (editingInvoice) {
        await axios.put(`${API}/invoices/${editingInvoice.id}`, payload);
        toast.success('Invoice updated');
      } else {
        await axios.post(`${API}/invoices`, payload);
        toast.success('Invoice created');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/invoices/${id}`);
      toast.success('Invoice deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const getPreviewPaymentDate = () => {
    if (!formData.invoice_date || !formData.customer_id) return null;
    const customer = customers.find(c => c.id === formData.customer_id);
    if (!customer) return null;
    const useHistorical = formData.payment_basis === 'historical'
      && customer.median_days_to_pay != null
      && (customer.pay_sample_size || 0) >= 3;
    const baseDays = useHistorical ? customer.median_days_to_pay : (customer.terms_days || 0);
    const totalDays = baseDays + (parseInt(formData.extra_days) || 0);
    const invoiceDate = parseISO(formData.invoice_date);
    const paymentDate = addDays(invoiceDate, totalDays);
    return {
      date: format(paymentDate, 'dd MMM yyyy'),
      month: format(paymentDate, 'MMM yyyy'),
      totalDays,
      basisUsed: useHistorical ? 'historical' : 'terms',
    };
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0
    }).format(value);
  };

  // ---------- Filtering & Selection ----------
  const filteredInvoices = invoices.filter((inv) => {
    // text search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matches = (inv.customer_name || '').toLowerCase().includes(q)
        || (inv.description || '').toLowerCase().includes(q)
        || String(inv.amount || '').includes(q);
      if (!matches) return false;
    }
    // due date range (expected_payment_date)
    if (dateFrom || dateTo) {
      if (!inv.expected_payment_date) return false;
      const due = parseISO(inv.expected_payment_date);
      if (dateFrom && due < startOfDay(parseISO(dateFrom))) return false;
      if (dateTo && due > endOfDay(parseISO(dateTo))) return false;
    }
    return true;
  });

  const filteredTotal = filteredInvoices.reduce((sum, inv) => sum + (inv.amount || 0), 0);
  const allFilteredSelected = filteredInvoices.length > 0
    && filteredInvoices.every((inv) => selectedIds.has(inv.id));

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredInvoices.forEach((inv) => next.delete(inv.id));
      } else {
        filteredInvoices.forEach((inv) => next.add(inv.id));
      }
      return next;
    });
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearDateRange = () => {
    setDateFrom('');
    setDateTo('');
  };

  const handleBulkDeleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkDeleting(true);
    try {
      const res = await axios.post(`${API}/invoices/bulk-delete`, { ids });
      toast.success(`${res.data.deleted} invoice${res.data.deleted === 1 ? '' : 's'} deleted`);
      setSelectedIds(new Set());
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoices');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleDeleteAllFiltered = async () => {
    const ids = filteredInvoices.map((inv) => inv.id);
    if (ids.length === 0) return;
    setBulkDeleting(true);
    try {
      const res = await axios.post(`${API}/invoices/bulk-delete`, { ids });
      toast.success(`${res.data.deleted} invoice${res.data.deleted === 1 ? '' : 's'} deleted`);
      setSelectedIds(new Set());
      setDeleteAllStep(0);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoices');
    } finally {
      setBulkDeleting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const preview = getPreviewPaymentDate();

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Invoices (AR)
            </h1>
            <p className="text-slate-500 mt-1">
              Track accounts receivable and expected payment timing
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="gap-2 bg-primary hover:bg-primary/90"
                onClick={() => handleOpenDialog()}
                disabled={customers.length === 0}
                data-testid="add-invoice-btn"
              >
                <Plus className="w-4 h-4" />
                Add Invoice
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {editingInvoice ? 'Edit Invoice' : 'Add Invoice'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="invoice_date">Invoice Date</Label>
                    <Input
                      id="invoice_date"
                      type="date"
                      value={formData.invoice_date}
                      onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                      data-testid="invoice-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="customer">Customer</Label>
                    <Select
                      value={formData.customer_id}
                      onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                    >
                      <SelectTrigger data-testid="invoice-customer-select">
                        <SelectValue placeholder="Select customer" />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map((customer) => (
                          <SelectItem key={customer.id} value={customer.id}>
                            {customer.name} ({customer.terms_days} days)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description (optional)</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Invoice description"
                    data-testid="invoice-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="amount">Amount (AUD)</Label>
                    <Input
                      id="amount"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="0.00"
                      data-testid="invoice-amount-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="extra_days">Extra Days</Label>
                    <div className="flex gap-2">
                      <Input
                        id="extra_days"
                        type="number"
                        value={formData.extra_days}
                        onChange={(e) => setFormData({ ...formData, extra_days: e.target.value })}
                        placeholder="0"
                        className="flex-1"
                        data-testid="invoice-extra-days-input"
                      />
                      {(() => {
                        // Calendar picker — converts a target date into the
                        // equivalent extra_days value relative to invoice_date
                        // + base_days (terms or historical).
                        const customer = customers.find(c => c.id === formData.customer_id);
                        const disabled = !formData.invoice_date || !customer;
                        const useHistorical = formData.payment_basis === 'historical'
                          && customer?.median_days_to_pay != null
                          && (customer?.pay_sample_size || 0) >= 3;
                        const baseDays = useHistorical
                          ? customer.median_days_to_pay
                          : (customer?.terms_days || 0);
                        const formalDate = formData.invoice_date && customer
                          ? addDays(parseISO(formData.invoice_date), baseDays)
                          : null;
                        const currentPickedDate = formalDate
                          ? addDays(formalDate, parseInt(formData.extra_days, 10) || 0)
                          : null;
                        return (
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                disabled={disabled}
                                title={disabled ? 'Select an invoice date and customer first' : 'Pick a specific due date'}
                                data-testid="invoice-pick-date-btn"
                              >
                                <CalendarDays className="w-4 h-4" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="p-0 w-auto" align="end">
                              <Calendar
                                mode="single"
                                selected={currentPickedDate || undefined}
                                onSelect={(d) => {
                                  if (!d || !formalDate) return;
                                  const delta = differenceInDays(d, formalDate);
                                  setFormData({ ...formData, extra_days: delta });
                                }}
                                initialFocus
                              />
                              <div className="px-3 py-2 border-t border-slate-200 text-[11px] text-slate-500 bg-slate-50">
                                Picks the exact payment date — adjusts Extra Days automatically.
                              </div>
                            </PopoverContent>
                          </Popover>
                        );
                      })()}
                    </div>
                    <p className="text-xs text-slate-500">Type extra days or pick a specific due date</p>
                  </div>
                </div>

                {/* Payment Basis */}
                {(() => {
                  const c = customers.find(x => x.id === formData.customer_id);
                  const sampleSize = (c && c.pay_sample_size) || 0;
                  const hasHistory = c && c.median_days_to_pay != null && sampleSize >= 3;
                  const hasLowSample = c && c.median_days_to_pay != null && sampleSize > 0 && sampleSize < 3;
                  return (
                    <div className="space-y-2">
                      <Label htmlFor="payment_basis">Payment date basis</Label>
                      <select
                        id="payment_basis"
                        value={formData.payment_basis}
                        onChange={(e) => setFormData({ ...formData, payment_basis: e.target.value })}
                        className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:bg-slate-800 dark:border-slate-700"
                        data-testid="invoice-payment-basis-select"
                      >
                        <option value="terms">
                          Formal payment terms{c ? ` (${c.terms_days || 0} days)` : ''}
                        </option>
                        <option value="historical" disabled={!hasHistory}>
                          {hasHistory
                            ? `Historical avg pay time (${c.median_days_to_pay} days, based on ${c.pay_sample_size} paid invoice${c.pay_sample_size === 1 ? '' : 's'})`
                            : hasLowSample
                              ? `Historical avg pay time (only ${sampleSize} paid invoice${sampleSize === 1 ? '' : 's'} — need 3+)`
                              : 'Historical avg pay time (no history yet)'}
                        </option>
                      </select>
                      {formData.payment_basis === 'historical' && !hasHistory && (
                        <p className="text-xs text-amber-700">
                          {hasLowSample
                            ? `Only ${sampleSize} paid invoice${sampleSize === 1 ? '' : 's'} so far — not enough history to be reliable. Falling back to formal terms.`
                            : 'No paid history yet for this customer — falls back to formal terms.'}
                        </p>
                      )}
                    </div>
                  );
                })()}

                {/* Payment Preview */}
                {preview && (
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                      <CalendarIcon className="w-4 h-4" />
                      <span className="font-medium">Expected Payment</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-500">Invoice Date</span>
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                      <span className="font-mono text-sm text-primary font-medium">{preview.date}</span>
                    </div>
                    <div className="mt-2 text-xs text-slate-500">
                      {preview.totalDays} days total • Cash allocated to <span className="font-semibold text-slate-700">{preview.month}</span>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline" data-testid="cancel-invoice-btn">Cancel</Button>
                </DialogClose>
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="save-invoice-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingInvoice ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {customers.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-amber-800 text-sm">
              Please add customers first before creating invoices.
            </p>
          </div>
        )}

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {invoices.length === 0 ? (
              <div className="empty-state py-16">
                <FileText className="empty-state-icon w-16 h-16 text-slate-300 mb-4" />
                <h3 className="empty-state-title text-lg font-semibold text-slate-900 mb-2">No invoices yet</h3>
                <p className="empty-state-text text-sm text-slate-500 max-w-md">
                  Add invoices to track expected payments and their impact on your cash flow.
                </p>
              </div>
            ) : (
              <>
                <div className="p-4 border-b space-y-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <Input
                      type="search"
                      placeholder="Search by customer, description, or amount..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="max-w-sm"
                      data-testid="invoices-search-input"
                    />
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-slate-500 whitespace-nowrap">Due From</Label>
                      <Input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className="h-9 w-36"
                        data-testid="invoices-date-from"
                      />
                      <Label className="text-xs text-slate-500 whitespace-nowrap">To</Label>
                      <Input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        className="h-9 w-36"
                        data-testid="invoices-date-to"
                      />
                      {(dateFrom || dateTo) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearDateRange}
                          className="h-8 px-2 text-slate-500"
                          data-testid="invoices-clear-range-btn"
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Summary + bulk actions */}
                  <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                    <div
                      className="inline-flex items-center gap-3 px-3 py-2 rounded-md bg-slate-50 border border-slate-200 dark:bg-slate-800 dark:border-slate-700"
                      data-testid="invoices-summary-panel"
                    >
                      <span className="text-xs text-slate-500 uppercase tracking-wider">
                        {(dateFrom || dateTo) ? 'In Range' : 'All'}
                      </span>
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100" data-testid="invoices-summary-count">
                        {filteredInvoices.length} invoice{filteredInvoices.length === 1 ? '' : 's'}
                      </span>
                      <span className="text-slate-300">·</span>
                      <span className="text-sm font-mono font-semibold text-emerald-700" data-testid="invoices-summary-total">
                        {formatCurrency(filteredTotal)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedIds.size > 0 && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              disabled={bulkDeleting}
                              data-testid="invoices-delete-selected-btn"
                            >
                              <Trash2 className="w-4 h-4 mr-1.5" />
                              Delete Selected ({selectedIds.size})
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete {selectedIds.size} invoice{selectedIds.size === 1 ? '' : 's'}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete the selected invoices. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={handleBulkDeleteSelected}
                                className="bg-red-600 hover:bg-red-700"
                                data-testid="invoices-confirm-delete-selected"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-700 border-red-200 hover:bg-red-50"
                        disabled={filteredInvoices.length === 0 || bulkDeleting}
                        onClick={() => setDeleteAllStep(1)}
                        data-testid="invoices-delete-all-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-1.5" />
                        Delete All{(dateFrom || dateTo || searchQuery) ? ' Filtered' : ''} ({filteredInvoices.length})
                      </Button>
                    </div>
                  </div>
                </div>
              <div className="overflow-x-auto">
                <Table data-testid="invoices-table">
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-10">
                        <Checkbox
                          checked={allFilteredSelected}
                          onCheckedChange={toggleSelectAll}
                          disabled={filteredInvoices.length === 0}
                          aria-label="Select all"
                          data-testid="invoices-select-all"
                        />
                      </TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Date</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Customer</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Description</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500 text-right">Amount</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500 text-center">Terms + Extra</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Expected Payment</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500 text-right w-32">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInvoices.map((invoice) => (
                      <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.has(invoice.id)}
                            onCheckedChange={() => toggleRow(invoice.id)}
                            aria-label="Select row"
                            data-testid={`invoices-select-${invoice.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {format(parseISO(invoice.invoice_date), 'dd MMM yyyy')}
                        </TableCell>
                        <TableCell className="font-medium text-slate-900">{invoice.customer_name}</TableCell>
                        <TableCell className="text-slate-600 max-w-xs truncate">{invoice.description || '-'}</TableCell>
                        <TableCell className="text-right font-mono text-sm cash-positive">
                          {formatCurrency(invoice.amount)}
                        </TableCell>
                        <TableCell className="text-center font-mono text-sm">
                          <div className="flex flex-col items-center">
                            <span>{invoice.terms_days} + {invoice.extra_days} = {invoice.total_days}d</span>
                            {invoice.historical_days_to_pay != null && invoice.historical_sample_size >= 3 && (
                              <span className="text-[10px] text-slate-500">
                                avg {invoice.historical_days_to_pay}d (n={invoice.historical_sample_size})
                              </span>
                            )}
                            <span className={`text-[10px] mt-0.5 px-1.5 rounded ${invoice.payment_basis === 'historical' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'}`}>
                              {invoice.payment_basis === 'historical' ? 'Historical' : 'Terms'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-mono text-sm">{format(parseISO(invoice.expected_payment_date), 'dd MMM yyyy')}</span>
                            <span className="text-xs text-slate-500">{format(parseISO(invoice.payment_month), 'MMM yyyy')}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenDialog(invoice)}
                              data-testid={`edit-invoice-${invoice.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-invoice-${invoice.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete this invoice? This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(invoice.id)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete-All double confirmation */}
      <AlertDialog
        open={deleteAllStep === 1}
        onOpenChange={(open) => { if (!open) setDeleteAllStep(0); }}
      >
        <AlertDialogContent data-testid="invoices-delete-all-step1">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {filteredInvoices.length} invoice{filteredInvoices.length === 1 ? '' : 's'}?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {(dateFrom || dateTo || searchQuery) ? 'all currently filtered invoices' : 'every invoice'} ({formatCurrency(filteredTotal)} total)?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="invoices-delete-all-step1-cancel">No</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); setDeleteAllStep(2); }}
              className="bg-red-600 hover:bg-red-700"
              data-testid="invoices-delete-all-step1-yes"
            >
              Yes, continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={deleteAllStep === 2}
        onOpenChange={(open) => { if (!open) setDeleteAllStep(0); }}
      >
        <AlertDialogContent data-testid="invoices-delete-all-step2">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700">This cannot be undone</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to permanently delete {filteredInvoices.length} invoice{filteredInvoices.length === 1 ? '' : 's'} ({formatCurrency(filteredTotal)} total).
              This action is irreversible. Are you absolutely sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="invoices-delete-all-step2-cancel">No, keep them</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAllFiltered}
              disabled={bulkDeleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="invoices-delete-all-step2-yes"
            >
              {bulkDeleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Yes, delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
